import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Select } from '../../../../components';
import { useClickOutside, useGetScorecardTemplateOptions, useSimActions } from '../../../../hooks';
import { ComponentSize } from '../../../../types';
import HoverableContent from './HoverableContent';

interface SimulationDetailsScorecardProps {
  personaId: string;
  currTemplateId?: string;
}

const SimulationDetailsScorecard = ({ personaId, currTemplateId }: SimulationDetailsScorecardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  // Used for immediate UI feedback after the template is updated.
  const [inputTemplateId, setInputTemplateId] = useState(currTemplateId);

  const inputRef = useRef<HTMLDivElement>(null);

  const { runEdit, isEditing: isEditLoading } = useSimActions();
  const { options: templateOptions } = useGetScorecardTemplateOptions({
    selectedTemplateId: inputTemplateId,
  });

  const templateName = templateOptions.find((option) => option.value === inputTemplateId)?.label || '';

  const onSubmit = useCallback(
    (newTemplateId?: string) => {
      if (newTemplateId === inputTemplateId) {
        // If the scorecard is the same, don't submit the edit.
        setIsEditing(false);
      } else {
        // Otherwise, submit the edit.
        runEdit({
          payload: { id: personaId, scorecardTemplateId: newTemplateId || null }, // Null is used to clear the scorecard.
          onSuccess: () => {
            setInputTemplateId(newTemplateId);
            setIsEditing(false);
          },
        });
      }
    },
    [personaId, inputTemplateId, runEdit]
  );

  // When the personaId changes, reset the editing state.
  useEffect(() => {
    setIsEditing(false);
    setInputTemplateId(currTemplateId);
  }, [personaId, currTemplateId]);

  useClickOutside(inputRef, () => setIsEditing(false));

  return isEditing ? (
    <Select
      ref={inputRef}
      disabled={isEditLoading}
      size={ComponentSize.X_SMALL}
      placeholder="Select scorecard"
      options={templateOptions}
      selected={inputTemplateId}
      onChange={onSubmit}
      clearable
    />
  ) : (
    <HoverableContent
      icon={Icon.EDIT}
      content={templateName}
      tooltip="Edit scorecard"
      onClick={() => setIsEditing(true)}
    />
  );
};

export default SimulationDetailsScorecard;
